import React, { useEffect } from 'react';

import { Container } from '@mui/material';
import { useToggle } from 'react-use';
import { observer } from 'mobx-react';

import classes from './NgzCustomerSelfSetupToEntity.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { NgzPageNotFound } from '../../../shared/containers/NgzPageNotFound/NgzPageNotFound';
import { getAccessParamsFromUrl } from '../../../shared/utils/apiUtils';
import { CompanyHeader } from '../../../shared/components/header-components/CompanyHeader/CompanyHeader';
import { useStepper } from '../../../shared/hooks/useStepper';
import { ExplanationSetupSteps } from '../../components/customer-self-setup-components';
import { QuestionnaireStep } from './containers/QuestionnaireStep/QuestionnaireStep';
import { PersonalInfoStep } from './containers/PersonalInfoStep/PersonalInfoStep';
import { SelectCoachStep } from './containers/SelectCoachStep/SelectCoachStep';
import { SelectSubscriptionStep } from './containers/SelectSubscriptionStep/SelectSubscriptionStep';
import { questionnaireStore } from '../../stores/customerSelfSetupStores/questionnaireStore';
import { personalInfoStore } from '../../stores/customerSelfSetupStores/personalInfoStore';
import { coachStore } from '../../stores/customerSelfSetupStores/coachStore';
import { entitySignUpStore } from '../../../shared/stores/entity/entitySignUpStore';
import {
  SetupCustomerStepper,
  StepOption,
} from '../../../shared/components/steppers/SetupCustomerStepper/SetupCustomerStepper';
import { entitySignUpService } from '../../../shared/services/entity/entitySignUpService';

// When make changes check if it is necessary to update the general registration flows NgzCustomerSelfSetup
const NgzCustomerSelfSetupToEntity = observer(() => {
  const [isShowExplanation, toggleShowExplanation] = useToggle(true);
  const stepperMethods = useStepper();
  const { activeStep } = stepperMethods;

  const { entityId, entityLogo, billingTypeFlags } = entitySignUpStore;

  const { accessParams, allParamsExist } = getAccessParamsFromUrl();

  const getEntityInfo = async () => {
    if (allParamsExist) {
      const entityData = await entitySignUpService.getEntityData(accessParams);

      entitySignUpStore.setEntityData(entityData);
    }
  };

  useEffect(() => {
    getEntityInfo();
  }, []);

  useEffect(() => {
    return () => {
      questionnaireStore.setDefaultValues();
      personalInfoStore.setDefaultValues();
      coachStore.setDefaultValues();
    };
  }, []);

  if (!allParamsExist) {
    return <NgzPageNotFound />;
  }

  if (!entityId) {
    return null;
  }

  const stepsConfig: StepOption[] = [
    {
      title:
        'Answer the following questions to help us create a custom meal plan specific to you and your goals',
      description: 'Please select one answer per question',
      step: <QuestionnaireStep stepperMethods={stepperMethods} />,
    },
    {
      title: 'Great! Now, please fill out your information to create a profile',
      description: 'All fields are required',
      step: <PersonalInfoStep stepperMethods={stepperMethods} />,
    },
    {
      title: 'Select coach you would like to work with',
      description: 'Use this list to find a coach, then select your coach to continue',
      step: <SelectCoachStep stepperMethods={stepperMethods} />,
    },
    {
      title: 'Select a plan below that fits your needs',
      description: billingTypeFlags.SELF_BILLING
        ? 'Select a subscription and proceed'
        : 'Select a subscription and proceed to the checkout with Stripe',
      step: <SelectSubscriptionStep stepperMethods={stepperMethods} />,
    },
  ];

  return (
    <>
      <CompanyHeader companyLogoUrl={entityLogo?.fileUrl} />
      <Container maxWidth="sm" className={classes.container}>
        {isShowExplanation ? (
          <ExplanationSetupSteps onStart={toggleShowExplanation} />
        ) : (
          <SetupCustomerStepper activeStep={activeStep} stepsConfig={stepsConfig} />
        )}
      </Container>
    </>
  );
});

angularizeWithProviders(NgzCustomerSelfSetupToEntity, 'ngzCustomerSelfSetupToEntity', {});

export { NgzCustomerSelfSetupToEntity };
